'use client';
import useEmblaCarousel from 'embla-carousel-react';
import { type FC, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { AgentLinePlay } from '@/app/components/icons/AgentLinePlay';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { BaseVideoPlayer } from '@/app/components/shared/VideoPlayer/BaseVideoPlayer';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { VideoPlayerRef } from '@/app/types';
import { cn } from '@/app/utils';
import { isContainerProcessing } from '../_hooks/use-task';
import { LoadingComponent } from './Canvas/components/LoadingComponent';
import { FailIcon } from './Icons/FaileIcon';

export const ViewResourceDialog: FC = () => {
  // 记录上一次的 agentLoadingNodeIds
  const prevLoadingNodeIdsRef = useRef<string[]>([]);
  const {
    agentLoadingNodeIds,
    agentNodeBusinessDataMap,
    agentIsOpenViewResourceDialog,
    agentViewResourceList,
    agentPropsActiveItemId,
    closeAgentViewResourceDialog,
    updateAgentViewResourceList,
  } = useStore((state) => ({
    agentLoadingNodeIds: state.agentLoadingNodeIds,
    agentNodeBusinessDataMap: state.agentNodeBusinessDataMap,
    agentIsOpenViewResourceDialog: state.agentIsOpenViewResourceDialog,
    agentViewResourceList: state.agentViewResourceList,
    agentPropsActiveItemId: state.agentPropsActiveItemId,
    closeAgentViewResourceDialog: state.closeAgentViewResourceDialog,
    updateAgentViewResourceList: state.updateAgentViewResourceList,
  }));

  useEffect(() => {
    const prevLoadingNodeIds = prevLoadingNodeIdsRef.current;
    prevLoadingNodeIdsRef.current = agentLoadingNodeIds;

    if (prevLoadingNodeIds.length <= agentLoadingNodeIds.length || !agentViewResourceList?.length) {
      return;
    }

    let hasUpdates = false;
    const updatedList = agentViewResourceList.map((item) => {
      // 判断该项是否刚完成加载
      if (!(prevLoadingNodeIds.includes(item.id) && !agentLoadingNodeIds.includes(item.id))) {
        return item;
      }

      const nodeData = agentNodeBusinessDataMap.get(item.id);
      if (!nodeData) {
        return item;
      }

      hasUpdates = true;

      return {
        ...item,
        status: nodeData.data.status,
        url: (nodeData.data.container_type === 'video' ? nodeData.data.videos?.url : item.url) || '',
        poster: (nodeData.data.container_type === 'video' ? nodeData.data.videos?.cover_url : item.url) || '',
        watermark_url: nodeData.data.container_type === 'video' ? nodeData.data.videos?.watermark_url : undefined,
      };
    });

    if (hasUpdates) {
      updateAgentViewResourceList(updatedList);
    }
  }, [agentLoadingNodeIds, agentViewResourceList, agentNodeBusinessDataMap, updateAgentViewResourceList]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  // 使用 Map 存储多个视频播放器的 ref
  const playerRefs = useRef<Map<string, VideoPlayerRef>>(new Map());

  // 跟踪当前播放器的暂停状态
  const [isPaused, setIsPaused] = useState(true);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const items = agentViewResourceList || [];

  // 获取当前选中项
  const currentItem = items[selectedIndex];

  // 设置 ref 的回调函数
  const setPlayerRef = useCallback((id: string, ref: VideoPlayerRef | null) => {
    if (ref) {
      playerRefs.current.set(id, ref);
    } else {
      playerRefs.current.delete(id);
    }
  }, []);

  // 获取当前视频的 ref
  const getCurrentPlayerRef = useCallback(() => {
    if (currentItem?.type === 'video') {
      return playerRefs.current.get(currentItem.id);
    }
    return null;
  }, [currentItem]);

  // 初始化选中的索引
  useEffect(() => {
    const initialIndex = items.findIndex((item) => item.id === agentPropsActiveItemId);
    if (initialIndex !== -1 && emblaApi) {
      emblaApi.scrollTo(initialIndex);
    }
  }, [items, agentPropsActiveItemId, emblaApi]);

  // 监听轮播状态变化，切换时暂停之前的视频
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const prevIndex = selectedIndex;
      const newIndex = emblaApi.selectedScrollSnap();

      // 暂停之前的视频
      const prevItem = items[prevIndex];
      if (prevItem?.type === 'video') {
        const prevPlayer = playerRefs.current.get(prevItem.id);
        prevPlayer?.pause();
      }

      setSelectedIndex(newIndex);
      setPrevBtnDisabled(!emblaApi.canScrollPrev());
      setNextBtnDisabled(!emblaApi.canScrollNext());
      setIsPaused(true); // 重置暂停状态
    };

    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, items, selectedIndex]);

  // 监听当前视频的播放/暂停状态
  useEffect(() => {
    const currentPlayer = getCurrentPlayerRef();
    if (currentPlayer?.player) {
      const handlePlay = () => {
        setIsPaused(false);
      };

      const handlePause = () => {
        setIsPaused(true);
      };

      // 监听播放和暂停事件
      currentPlayer.player.on('play', handlePlay);
      currentPlayer.player.on('pause', handlePause);

      // 同步初始状态
      setIsPaused(currentPlayer.player.paused);

      // 清理事件监听
      return () => {
        currentPlayer.player?.off('play', handlePlay);
        currentPlayer.player?.off('pause', handlePause);
      };
    }
  }, [getCurrentPlayerRef, currentItem]);

  // 当选中视频时自动播放
  useEffect(() => {
    if (currentItem?.type === 'video') {
      // 使用 setTimeout 确保视频播放器已经渲染完成
      const timer = setTimeout(() => {
        const currentPlayer = playerRefs.current.get(currentItem.id);
        if (currentPlayer) {
          currentPlayer.play();
          setIsPaused(false);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedIndex, currentItem]);

  // 点击上一张
  const handleClickPrevButton = (e: MouseEvent<HTMLDivElement>) => {
    if (prevBtnDisabled) return;
    e.stopPropagation();
    emblaApi?.scrollPrev();
  };

  // 点击下一张
  const handleClickNextButton = (e: MouseEvent<HTMLDivElement>) => {
    if (nextBtnDisabled) return;
    e.stopPropagation();
    emblaApi?.scrollNext();
  };

  // 点击底部缩略图
  const handleClickThumbnail = (index: number) => {
    emblaApi?.scrollTo(index);
  };

  // 点击播放按钮
  const handleClickPlayButton = (e: MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    const currentPlayer = getCurrentPlayerRef();
    if (currentPlayer) {
      currentPlayer.play();
      setIsPaused(false);
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={agentIsOpenViewResourceDialog}
      onOpenChange={(open) => {
        if (!open) {
          closeAgentViewResourceDialog();
        }
      }}
    >
      <DialogContent
        className="h-full max-h-full w-full max-w-full bg-system-white60"
        style={{
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="group relative flex w-full items-center justify-center">
          {/* 轮播容器 */}
          <div ref={emblaRef} className="h-full max-h-[568px] w-full max-w-[982px] overflow-hidden">
            <div className="flex h-full">
              {items.map((item, index) => (
                <div key={item.id} className="relative flex min-w-0 flex-none basis-full items-center justify-center">
                  <div className="flex h-[568px] w-[982px] items-center overflow-hidden rounded-8">
                    {isContainerProcessing(item.status) ? (
                      <LoadingComponent />
                    ) : item.status === 'failed' || item.status === 'canceled' ? (
                      <div className="flex h-full w-full items-center justify-center bg-black">
                        <FailIcon className="size-16 text-system-white" />
                      </div>
                    ) : item.type === 'video' ? (
                      <>
                        <MediaWithWatermark
                          width={item.width ?? 16}
                          height={item.height ?? 9}
                          showWatermark={SITE === 'china'}
                        >
                          <BaseVideoPlayer
                            ref={(ref) => setPlayerRef(item.id, ref)}
                            src={item.url}
                            poster={item.poster}
                            autoPlay={false}
                            hasAudio={true}
                            defaultVolume={0.5}
                            className={cn('h-full w-full')}
                          />
                        </MediaWithWatermark>
                        {selectedIndex === index && isPaused && (
                          <AgentLinePlay
                            currentColor="#000"
                            className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 z-10 size-12 cursor-pointer"
                            onClick={(e: React.MouseEvent<SVGSVGElement>) => handleClickPlayButton(e)}
                          />
                        )}
                      </>
                    ) : (
                      <img src={item.url} alt={item.name} className="h-full w-full object-contain" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 左右切换按钮 */}
          {items.length > 1 && (
            <>
              <div
                className="-translate-y-1/2 group absolute top-[50%] left-8 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-system-black64"
                onClick={handleClickPrevButton}
              >
                <BaseLineLeft className="size-5 text-system-white group-disabled:text-system-white20" />
              </div>
              <div
                className="-translate-y-1/2 group absolute top-[50%] right-8 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-system-black64"
                onClick={handleClickNextButton}
              >
                <BaseLineRight className="size-5 text-system-white group-disabled:text-system-white20" />
              </div>
            </>
          )}
        </div>

        {/* 底部资源list展示 */}
        <div className="-translate-x-1/2 absolute bottom-8 left-1/2 flex w-full max-w-[calc(100%-4rem)] px-4">
          <div className="scrollbar-hide flex h-[70px] w-full flex-row items-center justify-center gap-4 overflow-x-auto scroll-smooth">
            {items.map((item, index) => (
              <div
                key={item.id}
                className={cn(
                  'relative flex h-[70px] w-[121px] flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-8 border-[2px] transition-all',
                  selectedIndex === index ? 'border-system-white' : 'border-transparent hover:border-system-white/50',
                )}
                onClick={() => handleClickThumbnail(index)}
              >
                {isContainerProcessing(item.status) ? (
                  <LoadingComponent />
                ) : item.status === 'failed' || item.status === 'canceled' ? (
                  <FailIcon className="size-8 text-system-white" />
                ) : (
                  <>
                    <img src={item.poster} alt={item.name} className="h-full w-full object-cover" />
                    {item.type === 'video' && (
                      <AgentLinePlay
                        currentColor="#000"
                        className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 z-10 size-5"
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
